import { clsx } from 'clsx';
import {
  forwardRef,
  memo,
  useCallback,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type Ref,
  type RefObject,
} from 'react';
import { useIntl } from 'react-intl';

import { ChevronSmallLeftDoubleIcon, XmarkIcon } from '@dynatrace/strato-icons';

import { SidebarHint } from './CollapsedSidebarRail.js';
import * as styles from './SidebarLayout.sty.js';
import {
  type ExtractedContentSlot,
  type ExtractedSidebarSlot,
} from './slots.js';
import { useButtonFocusHandover } from './useFocusHandover.js';
import { useLayoutState } from './useLayoutState.js';
import { Button } from '../../../buttons/button/Button.js';
import { useId } from '../../../core/hooks/useId.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useVisualRefreshEnabled } from '../../../core/hooks/useVisualRefreshEnabled.js';
import { Drawer } from '../../../overlays/drawer/Drawer.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import { clampPanelWidth } from '../../split-layout/split-sizing-utils.js';
import { SplitLayout } from '../../split-layout/SplitLayout.js';
import { convertToPx } from '../../split-layout/width-utils.js';
import { useTeleport } from '../shared/useTeleport.js';

type TeleportPair = ReturnType<typeof useTeleport>;

// = Spacings.Size16; SplitLayout.Panel needs a plain number, not a CSS var
const COLLAPSED_RAIL_WIDTH = 16;
// visual refresh adds a 1px border-right (content-box, so it adds to the width)
const COLLAPSED_RAIL_BORDER_WIDTH = 1;

// mirrors SplitLayout's own resize-handle width (useSplitSizing)
const SPLIT_HANDLE_WIDTH = 8;

function SidebarLayoutInner({
  sidebar,
  content,
  containerRef,
  panelId,
  contentTeleport,
  panelTeleport,
}: {
  sidebar: ExtractedSidebarSlot;
  content: ExtractedContentSlot;
  containerRef: RefObject<HTMLDivElement | null>;
  panelId: string;
  contentTeleport: TeleportPair;
  panelTeleport: TeleportPair;
}) {
  const layout = useLayoutState({ sidebar, containerRef });
  const intl = useIntl();
  const focusHandover = useButtonFocusHandover();
  const isVisualRefreshEnabled = useVisualRefreshEnabled();

  const collapsedRailWidth = isVisualRefreshEnabled
    ? COLLAPSED_RAIL_WIDTH + COLLAPSED_RAIL_BORDER_WIDTH
    : COLLAPSED_RAIL_WIDTH;

  const [intendedWidth, setIntendedWidth] = useState<number>();
  const containerWidth = layout.containerWidth;

  const baseWidth =
    containerWidth === 0
      ? undefined
      : intendedWidth ??
        convertToPx(sidebar.config.defaultWidth, containerWidth);
  const panelWidth =
    baseWidth === undefined
      ? undefined
      : clampPanelWidth(baseWidth, containerWidth, {
          panelMin: sidebar.config.minWidth,
          panelMax: sidebar.config.maxWidth,
          contentMin: content.config.minWidth,
          handleWidth: sidebar.config.resizable ? SPLIT_HANDLE_WIDTH : 0,
        });

  const commitWidth = useCallback(
    (value: number) => {
      setIntendedWidth(value);
      sidebar.config.onResize?.(value);
    },
    [sidebar.config],
  );

  const expandSidebarLabel = intl.formatMessage({
    defaultMessage: 'Expand sidebar',
    id: 'NF0tLfJTokD/xw5B',
    description: 'Label for the button that expands the sidebar.',
  });

  const collapseSidebarLabel = intl.formatMessage({
    defaultMessage: 'Collapse sidebar',
    id: 'LJ0fyD2++VZgZ5g0',
    description: 'Label for the button that collapses the sidebar.',
  });

  const sidebarLabel = intl.formatMessage({
    defaultMessage: 'Sidebar',
    id: 'BYUJpW8TBonAxnYd',
    description: 'Accessible label for the sidebar panel.',
  });

  const setCollapsedWithFocusHandover = (value: boolean) => {
    layout.setCollapsed(value);
    focusHandover.setTarget(value ? 'expand' : 'collapse');
  };

  const contentNode = (
    <div
      ref={content.ref}
      {...content.domProps}
      className={clsx(styles.content, content.domProps.className)}
    >
      <contentTeleport.Target />
    </div>
  );

  // must stay in one SplitLayout, not per-mode, or it remounts and resets scroll (APPDEV-18829)
  const isSplit = layout.mode === 'split';
  const isDrawer = layout.mode === 'drawer';

  return (
    <>
      <SplitLayout>
        <SplitLayout.Panel
          {...(isSplit ? sidebar.domProps : {})}
          ref={isSplit ? (sidebar.ref as Ref<HTMLDivElement>) : undefined}
          minWidth={isSplit ? sidebar.config.minWidth : collapsedRailWidth}
          maxWidth={isSplit ? sidebar.config.maxWidth : collapsedRailWidth}
          defaultWidth={sidebar.config.defaultWidth}
          width={isSplit ? panelWidth : collapsedRailWidth}
          resizable={isSplit && sidebar.config.resizable}
          position="start"
          onResize={commitWidth}
          className={clsx(
            isSplit && styles.panel,
            isSplit && sidebar.domProps.className,
          )}
          // rail's expand button intentionally overflows the 16px column
          style={isSplit ? undefined : { overflow: 'visible' }}
          aria-label={isSplit ? sidebarLabel : undefined}
          id={isSplit ? panelId : undefined}
        >
          {isSplit ? (
            <panelTeleport.Target />
          ) : (
            <SidebarHint
              panelId={panelId}
              expandSidebarLabel={expandSidebarLabel}
              expandButtonRef={focusHandover.expandButtonRef}
              isCollapsed={isDrawer ? layout.collapsed : true}
              onCollapsedChange={setCollapsedWithFocusHandover}
            />
          )}
        </SplitLayout.Panel>
        <SplitLayout.ResizeHandle className={styles.resizeHandle}>
          <div className={styles.collapseButtonContainer}>
            <Tooltip text={collapseSidebarLabel}>
              <Button
                className={styles.collapseButton}
                aria-expanded="true"
                ref={focusHandover.collapseButtonRef}
                aria-controls={panelId}
                aria-label={collapseSidebarLabel}
                size="condensed"
                data-dt-children-name="ExpandCollapse"
                data-dt-properties="state:collapse"
                onClick={() => {
                  setCollapsedWithFocusHandover(true);
                }}
              >
                <Button.Prefix>
                  <ChevronSmallLeftDoubleIcon />
                </Button.Prefix>
              </Button>
            </Tooltip>
          </div>
        </SplitLayout.ResizeHandle>
        <SplitLayout.Content minWidth={content.config.minWidth}>
          {contentNode}
        </SplitLayout.Content>
      </SplitLayout>
      {isDrawer && (
        <Drawer
          {...sidebar.domProps}
          ref={sidebar.ref as Ref<HTMLDialogElement>}
          className={clsx(styles.drawer, sidebar.domProps.className)}
          aria-label={sidebarLabel}
          isDismissed={layout.collapsed}
          onDismiss={(state, _reason) => {
            setCollapsedWithFocusHandover(state);
          }}
          placement="left"
          width="wide"
        >
          <div className={styles.drawerContentContainer}>
            <div className={styles.drawerHeader}>
              <Button
                style={{ marginLeft: 'auto' }}
                ref={focusHandover.collapseButtonRef}
                aria-label={collapseSidebarLabel}
                data-dt-children-name="CloseX"
                onClick={() => {
                  setCollapsedWithFocusHandover(true);
                }}
              >
                <Button.Prefix>
                  <XmarkIcon />
                </Button.Prefix>
              </Button>
            </div>
            <div className={styles.drawerContent}>
              <panelTeleport.Target />
            </div>
          </div>
        </Drawer>
      )}
    </>
  );
}

/** @internal */
export type LayoutRootProps = {
  sidebar: ExtractedSidebarSlot | null;
  content: ExtractedContentSlot;
} & Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'content'>;

/** @internal */
export const SidebarLayout = memo(
  forwardRef<HTMLDivElement, LayoutRootProps>((props, ref) => {
    const { className: rootClassName, sidebar, content, ...rootProps } = props;

    const panelId = useId();
    const containerRef = useRef<HTMLDivElement>(null);
    const rootRef = useMergeRefs([ref, containerRef]);

    const contentTeleport = useTeleport();
    const panelTeleport = useTeleport();

    return (
      <>
        <contentTeleport.Source>{content.children}</contentTeleport.Source>
        {sidebar && (
          <panelTeleport.Source>{sidebar.children}</panelTeleport.Source>
        )}
        <div
          {...rootProps}
          ref={rootRef}
          className={clsx(styles.layoutRoot, rootClassName)}
        >
          {sidebar ? (
            <SidebarLayoutInner
              sidebar={sidebar}
              content={content}
              containerRef={containerRef}
              panelId={panelId}
              contentTeleport={contentTeleport}
              panelTeleport={panelTeleport}
            />
          ) : (
            <div
              ref={content.ref}
              {...content.domProps}
              className={clsx(styles.content, content.domProps.className)}
            >
              <contentTeleport.Target />
            </div>
          )}
        </div>
      </>
    );
  }),
);
SidebarLayout.displayName = 'SidebarLayout (forwardRef)';
